// src/context/AuthContext.jsx — global auth state (user + token), shared via React Context
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../services/api";

const AuthContext = createContext(null);

const readStoredUser = () => {
  const stored = localStorage.getItem("itontine_user") || sessionStorage.getItem("itontine_user");
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    localStorage.removeItem("itontine_user");
    sessionStorage.removeItem("itontine_user");
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(readStoredUser);
  const [loading, setLoading] = useState(true);

  const saveSession = (data, remember) => {
    localStorage.removeItem("itontine_token");
    localStorage.removeItem("itontine_user");
    sessionStorage.removeItem("itontine_token");
    sessionStorage.removeItem("itontine_user");
    if (remember) {
      localStorage.setItem("itontine_remember", "true");
      localStorage.setItem("itontine_token", data.token);
      localStorage.setItem("itontine_user", JSON.stringify(data.user));
    } else {
      localStorage.removeItem("itontine_remember");
      sessionStorage.setItem("itontine_token", data.token);
      sessionStorage.setItem("itontine_user", JSON.stringify(data.user));
    }
    setUser(data.user);
  };

  // Keep localStorage in sync whenever `user` changes.
  useEffect(() => {
    const remember = localStorage.getItem("itontine_remember") === "true";
    if (user && remember) {
      localStorage.setItem("itontine_user", JSON.stringify(user));
    } else if (user) {
      sessionStorage.setItem("itontine_user", JSON.stringify(user));
    } else {
      localStorage.removeItem("itontine_user");
      sessionStorage.removeItem("itontine_user");
    }
  }, [user]);

  // Refresh the cached user so role changes made by an admin take effect
  // without requiring stale localStorage data to be trusted.
  useEffect(() => {
    const token = localStorage.getItem("itontine_token") || sessionStorage.getItem("itontine_token");
    if (!token) {
      setLoading(false);
      return;
    }

    api.get("/users/me")
      .then(({ data }) => setUser(data))
      .catch((error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("itontine_token");
          localStorage.removeItem("itontine_user");
          sessionStorage.removeItem("itontine_token");
          sessionStorage.removeItem("itontine_user");
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password, remember = false) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      saveSession(data, remember);
      return data;
    } finally {
      setLoading(false);
    }
  };

  const register = async (payload) => {
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) formData.append(key, value);
      });
      // Let the browser add the multipart boundary; setting this header manually
      // prevents multer from parsing the text fields alongside the KYC file.
      const { data } = await api.post("/auth/register", formData);
      saveSession(data, true);
      return data.user;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("itontine_token");
    localStorage.removeItem("itontine_user");
    localStorage.removeItem("itontine_remember");
    sessionStorage.removeItem("itontine_token");
    sessionStorage.removeItem("itontine_user");
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/users/me");
      setUser(data);
    } catch {
      // token likely expired — the axios interceptor in api.js will redirect to /login
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for consuming the auth context from any component.
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
